export type BrowserDownloadOptions = {
  // 브라우저에 요청할 파일명. 요청일 뿐이므로 cross-origin 응답에서는 브라우저가 무시한다.
  filename?: string;
  // 다른 origin 의 URL(storage signed GET 등)인가.
  crossOrigin?: boolean;
};

/**
 * 임시 anchor 를 만들고 클릭해 브라우저 다운로드를 시작한다.
 *
 * `crossOrigin` 은 storage 의 signed URL 처럼 앱과 다른 origin 을 받을 때 켠다. 그 경우
 * 브라우저는 `download` 속성을 **무시하고 그 URL 로 이동**한다. 새 탭으로 열지 않으면 앱을
 * 벗어난다. 같은 origin(blob, object URL)은 `download` 가 그대로 적용되므로 새 탭이 필요 없다.
 *
 * 다른 origin 의 파일을 **파일명 그대로 저장**하려면 이 함수가 아니라 `downloadCrossOriginFile`
 * 을 쓴다. `crossOrigin` 을 직접 켜는 곳은 두 가지다.
 * - `downloadCrossOriginFile` 의 폴백 경로.
 * - 메모리에 한 번에 올릴 수 없는 큰 아티팩트(체크포인트 등).
 *
 * 이 경로로는 **디렉터리 구조를 복원할 수 없다**. 브라우저는 보안 때문에 파일명에서 경로 구분자를
 * 제거한다. 그래서 `cam0/episode_000001.mp4` 와 `cam1/episode_000001.mp4` 가 같은 이름으로 충돌한다.
 * 여러 파일을 원본 트리대로 받는 기능은 File System Access 기반 경로가 따로 소유한다.
 *
 * object URL 의 수명(`URL.revokeObjectURL`)은 호출자가 소유한다. 호출자는 클릭 뒤에 해제해야 한다.
 */
export function triggerBrowserDownload(url: string, options: BrowserDownloadOptions = {}): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  if (options.filename !== undefined) {
    anchor.download = options.filename;
  }
  if (options.crossOrigin === true) {
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
  }
  // 일부 브라우저는 문서에 없는 anchor 의 click() 을 무시한다. 그래서 문서에 붙였다가 뗀다.
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

// 저장에 성공했는지, 새 탭으로 열었는지 나타낸다. 호출부는 새 탭으로 열었을 때만 사용자에게 알린다.
export type CrossOriginDownloadResult = "saved" | "opened";

// object URL 을 해제하기 전 대기 시간. 클릭 직후 바로 해제하면 브라우저가 URL 을 읽기 전이라
// 다운로드가 취소될 수 있다. 오래 유지하면 영상 한 편이 그 시간 동안 메모리에 남는다.
// 이 값은 두 조건 사이에서 고른 값이다.
const OBJECT_URL_TTL_MS = 10_000;

/**
 * 다른 origin 의 파일(storage signed GET 등)을 파일명 그대로 디스크에 저장한다.
 *
 * 바이트를 먼저 받아 same-origin blob 으로 바꾸는 이유는 `download` 속성 때문이다.
 * cross-origin URL 에 이 속성을 적용하면 브라우저가 속성을 무시하고 URL 로 이동한다. 그러면
 * mp4 처럼 브라우저가 표시할 수 있는 형식은 저장되지 않고 새 탭에서 재생만 된다.
 *
 * 바이트를 읽지 못하면(storage CORS 미허용, 서명 만료, 오프라인) 예외를 던지지 않는다. 대신
 * 새 탭 경로로 폴백한다. 아무 일도 일어나지 않는 것보다 낫기 때문이다. 이 클릭은 사용자 제스처
 * 밖에서 일어나므로 팝업 차단에 걸릴 수 있다. 그래서 호출부는 `"opened"` 를 사용자에게 알린다.
 *
 * 이 방법은 **응답 전체를 한 번 메모리에 올린다**. 그래서 몇 MB 크기의 영상·이미지·문서에만 쓴다.
 * 크기 상한이 없는 아티팩트는 `triggerBrowserDownload` 의 `crossOrigin` 경로를 쓰거나,
 * 스트리밍으로 받는 `components/dataset/download` 스택을 쓴다.
 */
export async function downloadCrossOriginFile(
  url: string,
  filename: string,
): Promise<CrossOriginDownloadResult> {
  let objectUrl: string;
  try {
    // storage 에 직접 GET 한다. registry 를 거치지 않으므로 apiClient(인증 헤더)를 쓰지 않는다.
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`storage responded ${response.status}`);
    }
    objectUrl = URL.createObjectURL(await response.blob());
  } catch {
    triggerBrowserDownload(url, { filename, crossOrigin: true });
    return "opened";
  }
  // 클릭은 try 밖에 둔다. 여기서 난 오류로 폴백까지 실행하면 같은 파일을 두 번 받는다.
  triggerBrowserDownload(objectUrl, { filename });
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_TTL_MS);
  return "saved";
}
